/**
 * /dashboard/legislation/:recordId (§2.7, §2.7.2; screenshots/26, 28, 35–41).
 * One template for matters and votes. Painted from the snapshot the Worker inlines
 * (snapshot:legislation:{recordId}); live updates arrive as LegislationDeltas on `matter:{recordId}`.
 * Interactions: condensed sticky header (L7), stepper popovers → Timeline (L8), forecast becomes the
 * record (L9), keyboard (L10: j/k ↵ t w f 1–7 ?), tabs that never wait (L12).
 */
import { useEffect, useRef, useState } from "react";
import { connectMatter } from "../realtime/matterChannel";
import { ArrowLeft, Bell, Check, Crosshair, Download, ExternalLink, FileText, Mail, Phone, Plus, ScrollText, Share, Sparkles, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import { Face } from "../components/shared/Face";
import { findFollow, followsStore, useFollows } from "../stores/followsStore";
import { toastStore } from "../stores/toastStore";
import { can, type OrgRole, type UserGroup } from "../nav/navConfig";
import { ForecastChip, ForecastMeter, ProjectionBar, pct } from "../forecast/ForecastViews";
import { useListKeyboard, useScrolledPast } from "../hooks/useTabs";
import { prefersReducedMotion } from "../motion";
import { CouncilCard } from "./LegislationList";
import { Documents, DiscussedIn, ImpactReport, Lobbying, Timeline, alertOnFiling } from "./DetailTabs";
import { NoLobbying, NotEnoughHistory, NoWard, TabErrorBoundary } from "../components/shared/States";
import { enoughHistory } from "../forecast/forecast";
import { DETAIL_TABS, useLegislation, type DetailTab, type LegislationStore } from "./legislationStore";
import { ForecastByMember, InfluenceTable, WhipList } from "./Members";
import { councilLabel, groupByLabel, inPerspective } from "./logic";
import { AiChip, Kicker, LinkArrow, TabBar } from "./ui";
import type { LegislationDetail as Detail, LegislationDelta, Stage } from "./types";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { viewTop } from "../app/scroller";
import { Crumbs, sectionCrumb } from "../app/crumbs";

const STAGES: { id: Stage; label: string }[] = [
  { id: "filed", label: "Filed" }, { id: "introduced", label: "Introduced" }, { id: "committee", label: "Committee" },
  { id: "council", label: "City Council" }, { id: "final", label: "Final" },
];
const day = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const lastName = (name: string) => name.replace(/,? (Jr\.|Sr\.|III|II)$/, "").split(" ").pop() ?? name;

export function LegislationDetail({ store, group, role }: { store: LegislationStore; group: UserGroup; role: OrgRole | null }) {
  const d = useLegislation(store, (s) => s.detail);
  const tab = useLegislation(store, (s) => s.tab);
  const stepper = useRef<HTMLDivElement>(null);
  const page = useRef<HTMLDivElement>(null);
  // The bar appears as the tabs reach its bottom edge (bar height minus the gap above the tabs), so they never slide under it.
  const condensed = useScrolledPast(stepper, () => {
    const cs = page.current ? getComputedStyle(page.current) : null;
    // In window coordinates: below the breadcrumbs when the panel scrolls (app/scroller.ts).
    return cs ? viewTop() + (parseFloat(cs.getPropertyValue("--lg-bar-h")) || 48) - (parseFloat(cs.rowGap) || 0) : 0;
  });
  const [focusDate, setFocusDate] = useState<string>();
  const [compare, setCompare] = useState(false);
  const [sheet, setSheet] = useState(false);
  const followed = useFollows((s) => (d ? findFollow(s.follows, "legislation", d.recordId) : undefined));
  const toggleFollow = () => {
    if (!d) return;
    if (followed) void followsStore.getState().unfollow(followed.id);
    else void followsStore.getState().follow({ type: "legislation", refId: d.recordId, label: d.title });
  };
  const setTab = (t: DetailTab) => {
    store.getState().setTab(t);
    const u = new URL(location.href);
    if (t === "overview") u.searchParams.delete("tab"); else u.searchParams.set("tab", t);
    history.replaceState(history.state, "", u.pathname + u.search);
  };

  // Escape closes an open "why" (on phones it's a bottom sheet, screenshots/55).
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { const id = store.getState().expandedId; if (e.key === "Escape" && id) store.getState().toggleExpanded(id); };
    addEventListener("keydown", esc);
    return () => removeEventListener("keydown", esc);
  }, [store]);
  // Live: deltas from the `matter:{recordId}` channel (dispatched by the MatterRoom client as "matter:delta").
  useEffect(() => {
    const on = (e: Event) => { const { seq, delta } = (e as CustomEvent<{ seq: number; delta: LegislationDelta }>).detail; store.getState().applyDelta(seq, delta); };
    addEventListener("matter:delta", on);
    // Build step 17: the MatterRoom socket that dispatches them (realtime/matterChannel.ts).
    const rid = store.getState().detail?.recordId;
    const stop = rid ? connectMatter(rid, () => store.getState().seq) : () => {};
    return () => { removeEventListener("matter:delta", on); stop(); };
  }, [store]);
  // L9: when the roll call is recorded, a toast gives the final tally next to our forecast.
  useEffect(() => store.subscribe((s) => s.revealedAt, (at) => {
    const x = store.getState().detail;
    if (!at || !x?.rollCall) return;
    const outcome = x.rollCall.outcome === "failed" ? "Failed" : x.rollCall.outcome === "adopted" ? "Adopted" : "Passed";
    toastStore.getState().show({
      message: `Final: ${outcome} ${x.rollCall.yea}–${x.rollCall.nay}.${x.backtest ? ` Our forecast was ${x.backtest.projectedYes}–${x.backtest.projectedNo}.` : ""}`,
      actionLabel: "View", onAction: () => setTab("forecast"),
    });
  }), [store]); // eslint-disable-line react-hooks/exhaustive-deps

  // L10 keyboard: j/k rows, ↵ why, t target, w what-if, f follow, ? sheet (1–7 in the tab bar)
  useListKeyboard(page, {
    open: (id) => store.getState().toggleExpanded(id),
    t: can(group, role, "targetLists") ? (id) => { if (id) addTargets([id]); } : undefined,
    w: can(group, role, "whipCount") || can(group, role, "yourWay") ? () => { setTab("forecast"); store.getState().toggleWhatIf(); } : undefined,
    f: () => toggleFollow(),
    "?": () => setSheet(true),
  });

  if (!d) return null;
  const stageIdx = STAGES.findIndex((s) => s.id === d.stage);
  const goToStep = (date?: string) => {          // L8: stepper → Timeline, scroll, flash
    setTab("timeline");
    setFocusDate(undefined);
    requestAnimationFrame(() => {
      setFocusDate(date);
      requestAnimationFrame(() => document.getElementById(`tl-${date}`)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" }));
    });
  };
  const stepEntry = (i: number) => {
    const forStage = d.timeline.filter((e) => e.stage === STAGES[i].id);
    return forStage.find((e) => !e.upcoming) ?? forStage[0];
  };
  const addTargets = (ids: string[]) => {
    void fetch("/api/dashboard/targets", { method: "POST", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ recordId: d.recordId, personIds: ids }) }).catch(() => {});
    toastStore.getState().show({ message: `Added ${ids.length === 1 ? "1 member" : `${ids.length} members`} to your target list.` });
  };
  const share = async () => {
    const url = location.origin + location.pathname;
    if (navigator.share) { await navigator.share({ title: d.title, url }).catch(() => {}); return; }
    await navigator.clipboard?.writeText(url).catch(() => {});
    toastStore.getState().show({ message: "Link copied." });
  };
  const f = d.forecast;
  const followBtn = (small?: boolean) => (
    <button className={followed ? `btn btn--primary${small ? " lg-btn-sm" : ""}` : `btn${small ? " lg-btn-sm" : ""}`} aria-pressed={!!followed} onClick={toggleFollow}>
      {followed ? <Check size={14} aria-hidden /> : <Plus size={14} aria-hidden />}{followed ? "Following" : "Follow"}
    </button>
  );

  return (
    <div className="page lg-detail" ref={page}>
      {/* L7: condensed sticky header once the stepper scrolls out of view */}
      <div className="lg-condensed" data-on={condensed || undefined} aria-hidden={!condensed} inert={!condensed}>
        <b>{d.shortTitle || d.title}</b>
        <span className="mini-stepper">{STAGES.map((s, i) => <span key={s.id} data-state={i < stageIdx ? "done" : i === stageIdx ? "now" : "next"} />)}</span>
        <span className="spacer" />
        {f && !d.rollCall && <ForecastChip label={councilLabel(f)} perspective="yes" text={`${f.projectedYes}–${f.projectedNo}`} />}
        {followBtn(true)}
      </div>

      <Crumbs trail={[sectionCrumb("legislation"), { label: d.shortTitle || d.title }]} />
      <header className="detail-head">
        <div className="lg-title-block">
          <div className="lg-detail-meta">
            <i className="type-badge type-badge--legislation" aria-hidden><ScrollText size={11} /></i>
            <span>{d.recordId} · {d.type} · {d.categoryLong || d.category}</span><ConnectionStatus />
          </div>
          <h1>{d.title}</h1>
          <p className="lg-official">{d.officialTitle} · Sponsor: {d.sponsors.join(", ")} · {d.committee}</p>
        </div>
        <div className="actions">
          <a className="btn" href={`https://chicityclerkelms.chicago.gov/Matter/?matterId=${encodeURIComponent(d.matterId)}`} target="_blank" rel="noreferrer"><ExternalLink size={14} aria-hidden />Official record</a>
          <button className="btn" onClick={() => void share()}><Share size={14} aria-hidden />Share</button>
          {followBtn()}
        </div>
      </header>

      {/* L8: each step explains itself; click → Timeline entry */}
      <div className="stepper" ref={stepper}>
        {STAGES.map((s, i) => {
          const state = i < stageIdx ? "done" : i === stageIdx && d.stage !== "final" ? "now" : i === stageIdx ? "done" : "next";
          const e = stepEntry(i);
          const note = d.stageNotes[s.id] ?? (e ? day(e.date) : "");
          return (
            <button key={s.id} className="step" data-state={state} aria-describedby={e ? `step-pop-${s.id}` : undefined} onClick={() => goToStep(e?.date)}>
              <span className="step__bar" /><b>{s.label}</b><small data-label={s.label}>{note}</small>
              {e && <span role="tooltip" id={`step-pop-${s.id}`} className="step__pop">
                <span className="lg-pop__eyebrow">{day(e.date)} · {e.body}</span><b>{e.action}</b><span>{e.plain}</span>
              </span>}
            </button>
          );
        })}
      </div>

      <div className="lg-tabs-sticky" data-condensed={condensed || undefined}>
        <TabBar label="Legislation detail" active={tab} onChange={setTab} prefetch={(t) => prefetch(d, t)} tabs={[
          { id: "overview", label: "Overview" },
          { id: "forecast", label: "Forecast by member", count: d.lobbying.clients[0] ? d.lobbying.influence[d.lobbying.clients[0].id]?.length ?? 0 : undefined },
          { id: "impact", label: "Impact report" },
          { id: "timeline", label: "Timeline", count: d.timeline.length },
          { id: "documents", label: "Documents", count: d.documents.length },
          { id: "lobbying", label: "Lobbying & money", count: `${d.lobbying.clients.filter((c) => !c.isTeam).length} client${d.lobbying.clients.filter((c) => !c.isTeam).length === 1 ? "" : "s"}` },
          { id: "meetings", label: "Discussed in meetings", count: d.meetings.length },
        ] satisfies { id: DetailTab; label: string; count?: string | number }[]} />
      </div>

      <div className={tab === "documents" ? "cols cols--full" : "cols"}>
        <div className="cols__main tab-panel" key={tab} role="tabpanel">
          <TabErrorBoundary tabLabel={DETAIL_TAB_LABEL[tab]} fallbackHref={d.documents.find((x) => x.clerkUrl)?.clerkUrl ?? null}>
          {tab === "overview" && <Overview store={store} d={d} canSwitch={can(group, role, "yourWay")} onAll={() => setTab("forecast")} />}
          {tab === "forecast" && <ForecastByMember store={store} whatIfAllowed={can(group, role, "whipCount") || can(group, role, "yourWay")} />}
          {tab === "impact" && <ImpactReport d={d} />}
          {tab === "timeline" && <Timeline d={d} focusDate={focusDate} onCompare={() => { setCompare(true); setTab("documents"); }} onProjected={() => setTab("forecast")} />}
          {tab === "documents" && <Documents d={d} compare={compare} setCompare={setCompare} />}
          {tab === "lobbying" && <Lobbying store={store} />}
          {tab === "meetings" && <DiscussedIn d={d} />}
          </TabErrorBoundary>
        </div>
        {tab !== "documents" && (
          <aside className="cols__side">
            <SideColumn store={store} d={d} group={group} role={role} onToss={() => { setTab("forecast"); store.getState().setFilterGroup("tossup"); }} onTargets={addTargets} />
          </aside>
        )}
      </div>
      {sheet && <ShortcutSheet onClose={() => setSheet(false)} canTarget={can(group, role, "targetLists")} canWhatIf={can(group, role, "whipCount") || can(group, role, "yourWay")} />}
    </div>
  );
}

/** L12: hovering a tab warms what it needs (pdf.js and the current document), so clicking paints at once. */
function prefetch(d: Detail, t: DetailTab) {
  if (t === "documents") {
    void import("pdfjs-dist");
    const doc = d.documents.find((x) => x.version?.current) ?? d.documents[0];
    if (doc) void fetch(doc.url, { credentials: "include" }).catch(() => {});
  }
}
export { DETAIL_TABS };
/** Names for the tab error state ("We couldn't load Documents"), the same as the tab bar. */
const DETAIL_TAB_LABEL: Record<DetailTab, string> = { overview: "Overview", forecast: "Forecast by member", impact: "Impact report", timeline: "Timeline",
  documents: "Documents", lobbying: "Lobbying & money", meetings: "Discussed in meetings" };

function Overview({ store, d, canSwitch, onAll }: { store: LegislationStore; d: Detail; canSwitch: boolean; onAll: () => void }) {
  return (
    <div className="stack-32">
      {d.impact && (
        <section className="lg-what">
          <div className="section-head"><h2>What it would do</h2><AiChip>AI summary of the official text</AiChip></div>
          <p className="lg-summary">{d.impact.summary}</p>
          {d.impact.facts.length > 0 && <div className="grid-3 equal-cards lg-facts">{d.impact.facts.map((x) => <div key={x.label} className="fact"><b>{x.value}</b><span>{x.label}</span></div>)}</div>}
        </section>
      )}
      {d.lobbying.clients.some((c) => !c.isTeam) ? <InfluenceTable store={store} canSwitchPerspective={canSwitch} onAll={onAll} />
        : <NoLobbying onAlert={() => alertOnFiling(d)} />}
    </div>
  );
}

/* ---------------- Side column by user group (28) ---------------- */

function SideColumn({ store, d, group, role, onToss, onTargets }: {
  store: LegislationStore; d: Detail; group: UserGroup; role: OrgRole | null; onToss: () => void; onTargets: (ids: string[]) => void;
}) {
  if (group === "resident") return <ResidentSide d={d} />;
  if (group === "office") return <OfficeSide store={store} d={d} group={group} role={role} />;
  return <AdvocacySide store={store} d={d} group={group} role={role} onToss={onToss} onTargets={onTargets} />;
}

function AdvocacySide({ store, d, group, role, onToss, onTargets }: {
  store: LegislationStore; d: Detail; group: UserGroup; role: OrgRole | null; onToss: () => void; onTargets: (ids: string[]) => void;
}) {
  const client = d.lobbying.clients.find((c) => !c.isTeam) ?? d.lobbying.clients[0];
  const rows = client ? d.lobbying.influence[client.id] ?? [] : [];
  const toss = client ? groupByLabel(rows, "yes", client.position).tossup.map((x) => x.row.personId) : [];
  const onList = toss.filter((id) => d.team?.onTargetList.includes(id));
  const notContacted = toss.filter((id) => !d.team?.onTargetList.includes(id) && !d.team?.contacted.includes(id));
  const [added, setAdded] = useState(false);
  void store;
  return (<>
    {d.forecast && !d.rollCall && <CouncilCard item={d} kicker={`Council forecast${d.agenda ? ` · ${d.agenda.date}` : ""}`} onToss={onToss} />}
    {d.whereItStands && (
      <section className="card lg-where">
        <Kicker>Where it stands</Kicker>
        <p className="lg-where__body">{d.whereItStands.summary}</p>
        <div className="lg-where__lines">{d.whereItStands.lines.map((l) => <div key={l.date + l.text}><span>{day(l.date)}</span><span>{l.text}</span></div>)}</div>
      </section>
    )}
    {can(group, role, "targetLists") && (
      <section className="card lg-team">
        <header className="card__head"><Kicker>Your team</Kicker>{d.myPosition && <span className="chip chip--positive lg-chip-11">You {d.myPosition}</span>}</header>
        {d.forecast && <p className="lg-team__body">{toss.length} toss-ups · {onList.length} on your target list · {notContacted.length} not contacted</p>}
        {notContacted.length > 0 && (
          <button className="btn btn--primary btn--block" disabled={added} onClick={() => { onTargets(notContacted); setAdded(true); }}>
            {added ? <Check size={14} aria-hidden /> : <Crosshair size={14} aria-hidden />}Add {notContacted.length} toss-ups to target list
          </button>
        )}
        {can(group, role, "briefMe") && (
          <a className="btn btn--block" href={`/dashboard/legislation/${encodeURIComponent(d.recordId)}/brief`}>
            <Sparkles size={14} aria-hidden />Brief me before {d.agenda?.date ?? "the vote"}
          </a>
        )}
      </section>
    )}
    {can(group, role, "forecastAlerts") && (d.alerts?.length ?? 0) > 0 && (
      <section className="lg-alert-card">
        <b><Bell size={14} aria-hidden />Forecast moved</b>
        {d.alerts!.map((a) => (
          <p key={a.personId + a.at}>Ald. {lastName(a.name)}: {pct(a.from)}% → {pct(a.to)}% their way<span>{a.reason}</span></p>
        ))}
      </section>
    )}
    {d.documents.length > 0 && (
      <section className="card lg-docs-card">
        <Kicker>Documents · {d.documents.length}</Kicker>
        {d.documents.map((x) => <a key={x.name} className="doc-link" href={x.url} target="_blank" rel="noreferrer"><FileText size={14} aria-hidden />{x.name}</a>)}
      </section>
    )}
  </>);
}

function OfficeSide({ store, d, group, role }: { store: LegislationStore; d: Detail; group: UserGroup; role: OrgRole | null }) {
  const client = d.lobbying.clients.find((c) => !c.isTeam) ?? d.lobbying.clients[0];
  const rows = client ? d.lobbying.influence[client.id] ?? [] : [];
  const member = rows.find((r) => r.personId === d.myMember);
  const f = d.forecast;
  const wv = d.wardVoice;
  const share = (a: number, b: number) => Math.round((a / Math.max(1, a + b)) * 100);
  void store;
  return (<>
    {wv && can(group, role, "wardVoice") && (
      <section className="card lg-ward">
        <Kicker>Your ward said · How would you vote?</Kicker>
        <div className="ward-voice" role="img" aria-label={`Support ${wv.support}, oppose ${wv.oppose}`}>
          <span className="is-support" style={{ flexGrow: wv.support }} /><span className="is-oppose" style={{ flexGrow: wv.oppose }} />
        </div>
        <div className="row-between lg-ward__totals"><b>Support {share(wv.support, wv.oppose)}% · {wv.support}</b><b>Oppose {share(wv.oppose, wv.support)}% · {wv.oppose}</b></div>
        <p className="lg-card-note">{wv.support + wv.oppose} residents with a verified Ward {wv.ward} address. Totals only, never names. Updated live.</p>
      </section>
    )}
    {f && client && can(group, role, "whipCount") && !d.rollCall && (
      <section className="card lg-whipcard">
        <header className="card__head"><Kicker>Whip count{d.agenda ? ` · ${d.agenda.date}` : ""}</Kicker>
          <ForecastChip label={councilLabel(f)} perspective="yes" text={councilLabel(f) === "likely" ? "Likely passes" : councilLabel(f) === "unlikely" ? "Likely fails" : "Toss-up"} /></header>
        <ProjectionBar likelyYes={f.likelyYes} tossup={f.tossup} likelyNo={f.likelyNo} />
        <p className="lg-card__body">Needs {f.needed}. Toss-ups to call, most persuadable first:</p>
        <WhipList rows={rows} clientPosition={client.position} />
        <a className="btn btn--block" href={`/api/dashboard/legislation/${encodeURIComponent(d.recordId)}/whip.csv`} download><Download size={14} aria-hidden />Export whip list (CSV)</a>
      </section>
    )}
    {member && client && (
      <section className="card lg-others">
        <Kicker>What others see about your member</Kicker>
        <p className="lg-team__body">Ald. {lastName(member.name)} received ${member.sinceAmount.toLocaleString("en-US")} from {client.name} since 2019
          {member.pastYearAmount ? ` ($${member.pastYearAmount.toLocaleString("en-US")} in the past year)` : ""}.
          {member.moneyLinked.n ? ` Voted their way ${member.moneyLinked.k} of ${member.moneyLinked.n} times after receiving money.` : ""}</p>
        <div className="lg-others__meter">{enoughHistory(member.inputs) ? <><ForecastMeter f={member.forecast} perspective="their" showValue={false} /><b>{pct(member.forecast.p)}% their way</b></> : <NotEnoughHistory />}</div>
        <LinkArrow href={`/dashboard/legislation/${encodeURIComponent(d.recordId)}?draft=statement`}>Draft a statement</LinkArrow>
      </section>
    )}
  </>);
}

const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th"}`;

function ResidentSide({ d }: { d: Detail }) {
  const [stance, setStance] = useState<"support" | "oppose" | null>(d.myPosition ?? null);
  const choose = (v: "support" | "oppose") => {
    setStance(v);
    void fetch(`/api/dashboard/legislation/${encodeURIComponent(d.recordId)}/stance`, { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ position: v }) }).catch(() => {});
  };
  const a = d.myAlderperson;
  const client = d.lobbying.clients.find((c) => !c.isTeam) ?? d.lobbying.clients[0];
  const row = a && client ? d.lobbying.influence[client.id]?.find((r) => r.personId === a.personId) : undefined;
  const fc = row && client ? inPerspective(row.forecast, stance ? "your" : "yes", client.position, stance ?? undefined) : null;
  const persp = stance ? "your" : "yes";
  return (<>
    <section className="card lg-vote">
      <Kicker>How would you vote?</Kicker>
      <div className="lg-vote__btns">
        <button className={stance === "support" ? "btn btn--primary" : "btn"} aria-pressed={stance === "support"} onClick={() => choose("support")}><ThumbsUp size={16} aria-hidden />Support</button>
        <button className={stance === "oppose" ? "btn btn--primary" : "btn"} aria-pressed={stance === "oppose"} onClick={() => choose("oppose")}><ThumbsDown size={16} aria-hidden />Oppose</button>
      </div>
      <p className="lg-card-note">One person, one position. Your ward's total, never your name, is shared with your alderperson's office.</p>
    </section>
    {!a && <NoWard />}
    {a && (
      <section className="card lg-alder">
        <Kicker>Your alderperson</Kicker>
        <div className="lg-alder__who"><Face name={a.name} photoUrl={a.photoUrl} size={36} /><span><b>{a.name}</b><small>Ward {a.ward}{a.role ? ` · ${a.role}` : ""}</small></span></div>
        {fc && <div className="row-between"><ForecastChip f={fc} perspective={persp} /><span className="lg-11-muted">range {pct(fc.low)}–{pct(fc.high)}%</span></div>}
        {a.context && <p className="lg-team__body">{a.context}</p>}
        <div className="lg-script"><span className="eyebrow">Call script</span>
          <p>"Hi, I live in the {ordinal(a.ward)} Ward. I'm calling about the {d.shortTitle ? d.shortTitle.toLowerCase().replace(/&/g, "and") : d.title}, {d.recordId}{d.agenda ? `, up for a vote ${d.agenda.dateLong ?? d.agenda.date}` : ""}. I {stance ?? "support"} it because..."</p>
        </div>
        <div className="lg-alder__contact">
          {a.phone && <a className="btn btn--primary" href={`tel:${a.phone.replace(/[^\d+]/g, "")}`}><Phone size={16} aria-hidden />{a.phone}</a>}
          {a.email && <a className="btn" href={`mailto:${a.email}?subject=${encodeURIComponent(`${d.recordId} ${d.title}`)}`}><Mail size={16} aria-hidden />Email</a>}
        </div>
      </section>
    )}
    {d.impact?.forYou && (
      <section className="card lg-foryou">
        <Kicker>{d.impact.forYou.title}</Kicker>
        <ul>{d.impact.forYou.items.map((x) => <li key={x}><Check size={16} aria-hidden />{x}</li>)}</ul>
      </section>
    )}
  </>);
}

/* ---------------- Keyboard sheet (41 card 10) ---------------- */

function ShortcutSheet({ onClose, canTarget, canWhatIf }: { onClose: () => void; canTarget: boolean; canWhatIf: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref);
  const close = useRef(onClose);
  close.current = onClose;
  // Once: onClose is a new function on every parent render (live updates), and re-running this stole focus back.
  useEffect(() => {
    ref.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); close.current(); } };
    addEventListener("keydown", esc, true);
    return () => removeEventListener("keydown", esc, true);
  }, []);
  const rows: [string[], string][] = [
    [["j", "k"], "Next / previous row"], [["↵"], "Open the \"why\" for this member"],
    ...(canTarget ? [[["t"], "Add to target list"]] as [string[], string][] : []),
    ...(canWhatIf ? [[["w"], "What-if mode"]] as [string[], string][] : []),
    [["f"], "Follow this item"], [["1", "…", "7"], "Switch tabs"],
  ];
  return (
    <div className="lg-sheet-backdrop" onClick={onClose}>
      <div className="lg-sheet" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <button className="icon-btn lg-sheet__close" aria-label="Close" onClick={onClose}><X size={16} /></button>
        {rows.map(([keys, label]) => (
          <div key={label} className="lg-sheet__row"><span>{keys.map((k) => <kbd key={k} className="kbd">{k}</kbd>)}</span><span>{label}</span></div>
        ))}
      </div>
    </div>
  );
}

